// One chip per status with its count; a click filters the table to that status, a second click shows all again.
import { useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useListContext,
  useTranslate,
} from "ra-core";
import { FilterForm } from "@/components/admin/filter-form";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { cn } from "@/lib/utils";

import { PRE_LEAD_STATUSES, type PreLead, type PreLeadStatus } from "./types";

type Count = { status: PreLeadStatus; count: number; needs_reply: number };

export const PipelineStrip = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const { filterValues, setFilters } = useListContext();
  const { data: counts = [] } = useQuery({
    queryKey: ["pre_lead_counts"],
    queryFn: async () =>
      (
        await dataProvider.getList("pre_lead_counts", {
          pagination: { page: 1, perPage: PRE_LEAD_STATUSES.length },
          sort: { field: "status", order: "ASC" },
          filter: {},
        })
      ).data as unknown as Count[],
    refetchInterval: 60_000,
  });

  const choose = (status: PreLeadStatus) => {
    const { status: current, ...rest } = filterValues;
    setFilters(current === status ? rest : { ...rest, status });
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-2">
      <div className="flex min-w-0 flex-1 gap-2 overflow-x-auto">
        {PRE_LEAD_STATUSES.map((status) => {
          const row = counts.find((c) => c.status === status);
          const on = filterValues.status === status;
          const label = translate(`resources.pre_leads.statuses.${status}`);
          return (
            <button
              key={status}
              type="button"
              aria-pressed={on}
              onClick={() => choose(status)}
              className={cn(
                "flex min-h-11 min-w-24 shrink-0 cursor-pointer flex-col items-start rounded-lg border px-3 py-1.5 text-left text-xs text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                on && "border-primary bg-secondary text-secondary-foreground",
              )}
            >
              <span className="inline-flex items-center gap-1 whitespace-nowrap">
                {label}
                {Number(row?.needs_reply) > 0 && (
                  <>
                    <span
                      className="size-2 rounded-full bg-green-600"
                      aria-hidden
                    />{" "}
                    <span className="sr-only">
                      {translate("resources.pre_leads.needs_reply")}
                    </span>
                  </>
                )}
              </span>{" "}
              <span className="text-base font-semibold tabular-nums text-foreground">
                {Number(row?.count ?? 0)}
              </span>
            </button>
          );
        })}
      </div>
      <PreLeadFilters />
    </div>
  );
};

/** Search (company or person; the Supabase provider turns q into an OR of ilike) and the segment menu */
const PreLeadFilters = () => {
  const translate = useTranslate();
  // ponytail: segments of the latest 200 pre-leads, deduped here; a view of distinct names if that runs short
  const { data = [] } = useGetList<PreLead>("pre_leads", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "segment_name", order: "ASC" },
  });
  const segments = [
    ...new Set(data.map((p) => p.segment_name).filter(Boolean)),
  ] as string[];
  return (
    <FilterForm
      className="flex-nowrap"
      filters={[
        <SearchInput
          key="q"
          source="q"
          alwaysOn
          placeholder={translate("resources.pre_leads.search")}
          aria-label={translate("resources.pre_leads.search")}
        />,
        <SelectInput
          key="segment_name"
          source="segment_name"
          alwaysOn
          label={false}
          emptyText={translate("resources.pre_leads.segment_all")}
          choices={segments.map((name) => ({ id: name, name }))}
          translateChoice={false}
        />,
        <SelectInput
          key="contact"
          source="contact"
          alwaysOn
          label={false}
          emptyText={translate("resources.pre_leads.contact_all")}
          choices={[
            { id: "with", name: translate("resources.pre_leads.contact_with") },
            {
              id: "without",
              name: translate("resources.pre_leads.contact_without"),
            },
          ]}
          translateChoice={false}
        />,
      ]}
    />
  );
};
